import { useEffect, useMemo, useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import Avatar from '../components/Avatar'
import { useComptes, useNuits, usePersonnes, useRecapNuitees, useSetPresenceNuit } from '../api/hooks'
import { useHorizontalScrollSync } from '../components/useHorizontalScrollSync'
import type { Nuit, Personne as PersonneT } from '../api/types'

type Etat = 'absent' | 'confirme' | 'peut_etre' | 'refus'

const fmtEur = (n: number) =>
  n.toLocaleString('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 2 })

export default function Nuitees() {
  const nuitsQ = useNuits()
  const personnesQ = usePersonnes()
  const comptesQ = useComptes()
  const recapQ = useRecapNuitees()
  const mut = useSetPresenceNuit()
  const qc = useQueryClient()
  const [collapsed, setCollapsed] = useState<Set<number>>(new Set())

  // Nouvelle modale unique : clic sur une cellule → la modale s'ouvre SANS
  // muter quoi que ce soit. L'utilisateur choisit (1) un état parmi 4 puis
  // (2) la portée : cette nuit seulement / cette nuit + toutes les suivantes.
  const [pickerOpen, setPickerOpen] = useState<{
    personne_id: number
    prenom: string
    current_etat: Etat
    nuit_clickee: Nuit
    nuit_clickee_idx: number
    nuits_all: Nuit[]
  } | null>(null)
  // Cellules en cours de mutation → spinner affiché à la place du contenu.
  const [pendingCells, setPendingCells] = useState<Set<string>>(new Set())

  // === Sticky thead sur mobile : 2 tables (header + body), scroll horizontal synchronisé ===
  const headerScrollRef = useRef<HTMLDivElement | null>(null)
  const bodyScrollRef = useRef<HTMLDivElement | null>(null)
  useHorizontalScrollSync(bodyScrollRef, [headerScrollRef])

  const presenceMap = useMemo(() => {
    const m = new Map<string, Etat>()
    nuitsQ.data?.presences.forEach(p => {
      const etat: Etat = p.refus
        ? 'refus'
        : p.tentatif
        ? 'peut_etre'
        : 'confirme'
      m.set(`${p.personne_id}-${p.nuit_id}`, etat)
    })
    return m
  }, [nuitsQ.data])

  const lignesParCompte = useMemo(() => {
    const map = new Map<number, { compte_nom: string; rang: number; personnes: PersonneT[] }>()
    comptesQ.data?.forEach(c => map.set(c.id, { compte_nom: c.nom, rang: c.rang, personnes: [] }))
    personnesQ.data?.forEach(p => {
      const entry = map.get(p.compte_id)
      if (entry && p.actif) entry.personnes.push(p)
    })
    return Array.from(map.entries())
      .filter(([, v]) => v.personnes.length > 0)
      .sort((a, b) => a[1].rang - b[1].rang || a[1].compte_nom.localeCompare(b[1].compte_nom))
  }, [comptesQ.data, personnesQ.data])

  // Index personne pour récupérer le prénom dans la modale.
  // ATTENTION : ce useMemo et le useEffect ci-dessous DOIVENT être appelés à chaque
  // render (Rules of Hooks) → ils sont placés AVANT le early return de chargement.
  const personneById = useMemo(() => {
    const m = new Map<number, PersonneT>()
    personnesQ.data?.forEach(p => m.set(p.id, p))
    return m
  }, [personnesQ.data])

  // Fermer la modale à l'ESC (hook avant le early return)
  useEffect(() => {
    if (!pickerOpen) return
    const onKey = (e: KeyboardEvent) => {
      // Ne pas fermer si une propagation est en cours pour éviter d'orphan des spinners.
      if (e.key === 'Escape' && pendingCells.size === 0) setPickerOpen(null)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [pickerOpen, pendingCells])

  if (!nuitsQ.data || !personnesQ.data || !comptesQ.data) {
    return <p className="text-ocean-700 italic">Chargement…</p>
  }

  const nuits = nuitsQ.data.nuits
  // Pour chaque personne : montant confirmé + montant "max" si tous les peut-être sont confirmés
  const montantParPers = new Map(
    recapQ.data?.personnes.map(p => [
      p.personne_id,
      { du: p.montant_du, du_max: p.montant_du_max, nb_tent: p.nb_nuits_tentatifs },
    ]) ?? [],
  )
  const totalCompte = new Map(recapQ.data?.comptes.map(c => [c.compte_id, c]) ?? [])

  const toggleFamille = (cid: number) => {
    setCollapsed(s => {
      const n = new Set(s)
      n.has(cid) ? n.delete(cid) : n.add(cid)
      return n
    })
  }
  const allCollapsed = collapsed.size === lignesParCompte.length
  const setAll = (col: boolean) =>
    setCollapsed(col ? new Set(lignesParCompte.map(([cid]) => cid)) : new Set())

  // Payload à envoyer au backend pour un état donné
  const payloadFor = (next: Etat) => ({
    present: next === 'confirme' || next === 'peut_etre',
    tentatif: next === 'peut_etre',
    refus: next === 'refus',
  })

  // Clic sur une cellule → ouvre la modale sans muter (la mutation est faite
  // après que l'utilisateur ait choisi état + portée).
  const handleCellClick = (personne_id: number, cible_id: number) => {
    const idx = nuits.findIndex(n => n.id === cible_id)
    if (idx < 0) return
    const personne = personneById.get(personne_id)
    if (!personne) return
    const current_etat = presenceMap.get(`${personne_id}-${cible_id}`) ?? 'absent'
    setPickerOpen({
      personne_id,
      prenom: personne.prenom,
      current_etat,
      nuit_clickee: nuits[idx],
      nuit_clickee_idx: idx,
      nuits_all: nuits,
    })
  }

  // Applique un état à une liste EXPLICITE de nuits. La modale décide de la
  // portée (cette nuit / cette nuit + suivantes / plage de dates début→fin)
  // et fournit ici les nuit_ids à muter. Pendant la propagation, chaque
  // cellule concernée affiche un spinner.
  const applyEtat = async (target: Etat, target_nuit_ids: number[]) => {
    if (!pickerOpen) return
    if (target_nuit_ids.length === 0) {
      setPickerOpen(null)
      return
    }
    const { personne_id } = pickerOpen

    // Dédoublonner (défensif : au cas où le picker passerait un doublon)
    const uniq = Array.from(new Set(target_nuit_ids))
    const keys = uniq.map(nid => `${personne_id}-${nid}`)
    setPendingCells(prev => {
      const s = new Set(prev)
      keys.forEach(k => s.add(k))
      return s
    })

    try {
      const payload = payloadFor(target)
      // Promise.all — chaque PUT est indépendant et React Query coalesce les
      // invalidations. Si UNE requête échoue, on le remonte pour ne pas fermer
      // la modale silencieusement (l'utilisateur doit voir que ça n'a pas pris).
      const results = await Promise.allSettled(
        uniq.map(nid =>
          mut.mutateAsync({ personne_id, cible_id: nid, ...payload }),
        ),
      )
      const failures = results.filter(r => r.status === 'rejected').length
      if (failures > 0) {
        // Message utilisateur explicite — évite le "silent fail" possible
        // sur un timeout réseau ou un 5xx sporadique.
        window.alert(
          `Attention : ${failures}/${uniq.length} modification(s) n'ont pas pu être enregistrées. `
          + `Merci de réessayer.`,
        )
      }
      // Force le re-fetch de /nuits ET de /recap pour garantir que l'UI
      // reflète exactement l'état côté serveur (défensif contre un cache stale).
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['nuits'], refetchType: 'active' }),
        qc.invalidateQueries({ queryKey: ['recap'], refetchType: 'active' }),
        qc.invalidateQueries({ queryKey: ['assignations'], refetchType: 'active' }),
        qc.invalidateQueries({ queryKey: ['couchages', 'alertes'], refetchType: 'active' }),
      ])
    } finally {
      setPendingCells(prev => {
        const s = new Set(prev)
        keys.forEach(k => s.delete(k))
        return s
      })
      setPickerOpen(null)
    }
  }

  return (
    <div className="space-y-4">
      <section className="card p-4 sm:p-5">
        {/* Ligne 1 : titre + bouton replier/déplier + pills */}
        <div className="flex flex-wrap gap-3 items-start justify-between mb-3">
          <div>
            <h2 className="text-lg sm:text-xl font-bold text-ocean-900">
              🌙 Nuitées · 14 nuits à répartir
            </h2>
            <p className="text-xs sm:text-sm text-ocean-700 mt-1">
              Chacun et chacune peut <strong>exprimer un souhait de présence</strong> en cliquant
              sur chaque case.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => setAll(!allCollapsed)}
              className="btn-ghost btn-sm border border-ocean-200 hover:border-ocean-400"
            >
              {allCollapsed ? '▸ Tout déplier' : '▾ Tout replier'}
            </button>
            <div className="flex flex-wrap gap-2 text-xs">
              <Pill
                label="À répartir / semaine"
                value={recapQ.data?.cout_par_semaine ? fmtEur(recapQ.data.cout_par_semaine) : fmtEur(recapQ.data?.total_a_repartir ?? 0)}
              />
              {/* Prix moyens RÉELS renvoyés par le backend. Le prix enfant n'est pas
                  « prix adulte × 0.8 » : l'enfant paie 80 % du tarif de base, l'adulte
                  paie la base + le report du reliquat enfants/bébés. */}
              <Pill label="Prix adulte & ado / nuit" value={recapQ.data?.prix_par_poids ? fmtEur(recapQ.data.prix_par_poids) : '—'} />
              <Pill label="Prix enfant / nuit" value={recapQ.data?.prix_nuit_enfant ? fmtEur(recapQ.data.prix_nuit_enfant) : '—'} />
            </div>
          </div>
        </div>

        {/* Bloc "4 états" en PLEINE LARGEUR */}
        <div className="text-xs sm:text-sm bg-ocean-50 border border-ocean-100 rounded-lg px-3 py-2 leading-snug text-ocean-800">
          <div className="font-bold mb-1.5">4 états par cellule (cliquer ouvre un menu de choix) :</div>
          <ul className="space-y-1.5">
            <li className="grid grid-cols-[24px_1fr] items-center gap-2">
              <span className="inline-flex items-center justify-center w-6 h-6 rounded border border-ocean-200 bg-white text-ocean-400 text-xs">·</span>
              <span><strong>Vide</strong> — pas encore répondu</span>
            </li>
            <li className="grid grid-cols-[24px_1fr] items-center gap-2">
              <span className="inline-flex items-center justify-center w-6 h-6 rounded border border-ocean-600 bg-gradient-ocean text-white text-xs font-bold">✓</span>
              <span><strong>Confirmé</strong> — je serai là (compté dans le prix et la chambre)</span>
            </li>
            <li className="grid grid-cols-[24px_1fr] items-center gap-2">
              <span className="inline-flex items-center justify-center w-6 h-6 rounded border border-amber-400 bg-amber-200 text-amber-800 text-xs font-bold">↔</span>
              <span><strong>Provisoire (peut-être)</strong> — je peux me retirer si trop de monde</span>
            </li>
            <li className="grid grid-cols-[24px_1fr] items-center gap-2">
              <span className="inline-flex items-center justify-center w-6 h-6 rounded border border-coral-600 bg-coral-500 text-white text-xs font-bold">✕</span>
              <span><strong>Ne vient pas</strong> — refus explicite (différent de « pas répondu »)</span>
            </li>
          </ul>
        </div>

        {/* Bloc "Attention" en PLEINE LARGEUR */}
        <p className="text-xs sm:text-sm text-coral-800 mt-2 bg-coral-50 border border-coral-200 rounded-lg px-3 py-2 leading-snug">
          ⚠ <strong>Attention</strong> : si des <strong>limites de couchage</strong> apparaissent
          (alertes dans l'onglet <strong>Couchages</strong> 🛏️), il faudra <strong>adapter
          sa présence</strong> — passer du « ✓ confirmé » à « ↔ provisoire » ou retirer la coche
          pour laisser la place à d'autres.
        </p>
        <p className="text-[11px] text-ocean-600/80 italic mt-2">
          💡 Dans la colonne <strong>Dû</strong>, le montant noir = nuits actuellement confirmées.
          Le <span className="text-amber-700 font-semibold">↗ montant ambre</span> dessous = ce que tu paierais
          <em> si tu confirmais aussi tes ↔ provisoires</em>.
        </p>
      </section>

      <div className="card">
        {/* En-tête sticky : table séparée, scroll horizontal sync avec le body via JS */}
        <div
          ref={headerScrollRef}
          className="sticky top-0 sm:top-[49px] z-30 overflow-x-hidden bg-ocean-50 shadow-[0_2px_4px_rgba(15,76,110,0.08)] rounded-t-2xl"
        >
          <table className="text-xs sm:text-sm w-max lg:w-full lg:table-fixed">
            <colgroup>
              <col className="w-[170px] lg:w-[180px]" />
              {nuits.map(n => <col key={n.id} className="w-[44px]" />)}
              <col className="w-[60px] lg:w-[50px]" />
              <col className="w-[90px] lg:w-[80px]" />
            </colgroup>
            <thead>
              <tr>
                <th className="text-left px-2 py-2 sticky-col z-10 bg-ocean-50">Personne</th>
                {nuits.map(n => {
                  const [debut, fin] = n.libelle.split('→').map(s => s.trim())
                  return (
                    <th
                      key={n.id}
                      className="px-0.5 py-1 text-center font-normal text-ocean-700 align-bottom bg-ocean-50"
                    >
                      <div className="text-[10px] leading-tight whitespace-nowrap">{debut.split(' ').slice(0, 2).join(' ')}</div>
                      <div className="text-[8.5px] opacity-60 whitespace-nowrap">→ {fin.split(' ').slice(0, 2).join(' ')}</div>
                    </th>
                  )
                })}
                <th className="px-1 py-2 text-right bg-ocean-50">Nuits</th>
                <th className="px-1 py-2 text-right bg-ocean-50">Dû</th>
              </tr>
            </thead>
          </table>
        </div>

        {/* Corps : seul élément avec scroll horizontal actif.
            pt-1 = micro-buffer pour que la première ligne ne « colle » pas au header sticky. */}
        <div ref={bodyScrollRef} className="overflow-x-auto lg:overflow-x-visible rounded-b-2xl pt-1">
          <table className="text-xs sm:text-sm w-max lg:w-full lg:table-fixed">
            <colgroup>
              <col className="w-[170px] lg:w-[180px]" />
              {nuits.map(n => <col key={n.id} className="w-[44px]" />)}
              <col className="w-[60px] lg:w-[50px]" />
              <col className="w-[90px] lg:w-[80px]" />
            </colgroup>
            <tbody>
              {lignesParCompte.map(([cid, fam]) => {
                const isCollapsed = collapsed.has(cid)
                const tot = totalCompte.get(cid)
                return (
                  <FamilleBlock
                    key={cid}
                    compte_id={cid}
                    nom={fam.compte_nom}
                    personnes={fam.personnes}
                    nuits={nuits}
                    presenceMap={presenceMap}
                    montantParPers={montantParPers}
                    totalFamille={tot ? {
                      nb_nuits: tot.nb_nuits_total,
                      montant: tot.montant_du,
                      montant_max: tot.montant_du_max,
                      nb_tentatifs: tot.nb_nuits_tentatifs_total,
                      nb_pers: tot.nb_personnes_presentes,
                    } : null}
                    nbColonnes={nuits.length}
                    collapsed={isCollapsed}
                    onToggleFamille={() => toggleFamille(cid)}
                    onCellClick={handleCellClick}
                    pendingKey={(pid, nid) => pendingCells.has(`${pid}-${nid}`)}
                  />
                )
              })}
            </tbody>
          </table>
        </div>
      </div>

      {pickerOpen && (
        <EtatPickerModal
          proposal={pickerOpen}
          working={pendingCells.size > 0}
          onCancel={() => pendingCells.size === 0 && setPickerOpen(null)}
          onApply={applyEtat}
        />
      )}
    </div>
  )
}

/** Petit loader circulaire utilisé dans une cellule pendant la mutation. */
function CellSpinner() {
  return (
    <span
      role="status"
      aria-label="Application en cours…"
      className="inline-block w-4 h-4 rounded-full border-2 border-current border-t-transparent animate-spin"
    />
  )
}

/** Badge visuel d'un état dans la modale (icône + libellé court). */
function EtatBadge({ etat }: { etat: Exclude<Etat, 'absent'> }) {
  if (etat === 'confirme') {
    return (
      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-gradient-ocean text-white text-sm font-bold shadow-soft">
        <span>✓</span><span>Confirmé</span>
      </span>
    )
  }
  if (etat === 'peut_etre') {
    return (
      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-amber-200 text-amber-800 border border-amber-400 text-sm font-bold shadow-soft">
        <span>↔</span><span>Provisoire</span>
      </span>
    )
  }
  return (
    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-coral-500 text-white border border-coral-600 text-sm font-bold shadow-soft">
      <span>✕</span><span>Ne vient pas</span>
    </span>
  )
}

/** Bouton-choix d'un état dans la modale : style cellule + libellé.
 *  - isCurrent : c'est l'état actuel de la cellule cliquée (= info contextuelle).
 *  - isSelected : l'utilisateur vient de cliquer ce bouton à l'étape 1 (= focus visuel fort). */
function EtatChoiceButton({
  etat, isCurrent = false, isSelected = false, disabled, onClick,
}: {
  etat: Etat
  isCurrent?: boolean
  isSelected?: boolean
  disabled: boolean
  onClick: () => void
}) {
  const cfg: Record<Etat, { label: string; icon: string; cls: string }> = {
    absent:    { label: 'Laisser vide',  icon: '·', cls: 'bg-white text-ocean-500 border-ocean-200 hover:bg-ocean-50' },
    confirme:  { label: 'Confirmé',      icon: '✓', cls: 'bg-gradient-ocean text-white border-ocean-600 hover:opacity-90' },
    peut_etre: { label: 'Provisoire',    icon: '↔', cls: 'bg-amber-200 text-amber-800 border-amber-400 hover:bg-amber-300' },
    refus:     { label: 'Ne vient pas',  icon: '✕', cls: 'bg-coral-500 text-white border-coral-600 hover:bg-coral-600' },
  }
  const c = cfg[etat]
  const ring = isSelected
    ? 'ring-4 ring-coral-400 ring-offset-2 scale-[1.02]'
    : isCurrent
    ? 'ring-2 ring-ocean-400 ring-offset-2'
    : ''
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`relative flex items-center gap-2.5 px-3 py-2.5 rounded-lg border-2 shadow-soft font-semibold transition disabled:opacity-50 ${c.cls} ${ring}`}
    >
      <span className="inline-flex items-center justify-center w-7 h-7 rounded-md bg-black/15 font-bold text-base">
        {c.icon}
      </span>
      <span className="text-sm sm:text-base">{c.label}</span>
      {isSelected && (
        <span className="ml-auto text-[10px] uppercase tracking-wide font-bold opacity-90 bg-white/30 rounded px-1.5 py-0.5">
          choisi ✓
        </span>
      )}
      {!isSelected && isCurrent && (
        <span className="ml-auto text-[10px] uppercase tracking-wide font-bold opacity-80">
          actuel
        </span>
      )}
    </button>
  )
}

/** Représente l'état actuel d'une cellule avec une couleur neutre/correcte. */
function CurrentEtatBadge({ etat }: { etat: Etat }) {
  if (etat === 'absent') {
    return (
      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-ocean-50 text-ocean-600 border border-ocean-200 text-sm font-bold">
        <span>·</span><span>Pas encore répondu</span>
      </span>
    )
  }
  return <EtatBadge etat={etat as Exclude<Etat, 'absent'>} />
}

/** Modale en 2 étapes : (1) choix de l'état parmi 4, (2) choix de la portée. */
/** Nettoie le libellé d'une nuit pour l'afficher dans les <select> (2 mots suffisent). */
function nuitLibelleCourt(n: Nuit): string {
  const debut = n.libelle.split('→')[0].trim()
  return debut.split(' ').slice(0, 3).join(' ') // ex : "Sam 25 juil."
}

function EtatPickerModal({
  proposal, working, onCancel, onApply,
}: {
  proposal: {
    prenom: string
    current_etat: Etat
    nuit_clickee: Nuit
    nuit_clickee_idx: number
    nuits_all: Nuit[]
  }
  /** True pendant que des mutations sont en cours (= spinners actifs). */
  working: boolean
  onCancel: () => void
  onApply: (target: Etat, target_nuit_ids: number[]) => void
}) {
  const [chosenEtat, setChosenEtat] = useState<Etat | null>(null)
  const nuits_all = proposal.nuits_all
  const nuit_clickee_idx = proposal.nuit_clickee_idx
  const nuits_suivantes = nuits_all.slice(nuit_clickee_idx + 1)
  const n = nuits_suivantes.length
  const debutClick = nuitLibelleCourt(proposal.nuit_clickee)
  // Plage début → fin. Par défaut on démarre sur la nuit cliquée, et fin = dernière nuit.
  const [rangeStartIdx, setRangeStartIdx] = useState<number>(nuit_clickee_idx)
  const [rangeEndIdx, setRangeEndIdx] = useState<number>(nuits_all.length - 1)
  // Auto-correction : si fin < début, on aligne.
  const effStart = Math.min(rangeStartIdx, rangeEndIdx)
  const effEnd = Math.max(rangeStartIdx, rangeEndIdx)
  const rangeCount = effEnd - effStart + 1
  const rangeNuitIds = nuits_all.slice(effStart, effEnd + 1).map(nu => nu.id)

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-3 bg-ocean-900/50 backdrop-blur-sm anim-in"
      role="dialog"
      aria-modal="true"
      onClick={onCancel}
    >
      <div
        className="card relative max-w-lg w-full p-5 sm:p-6 max-h-[92vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={onCancel}
          disabled={working}
          aria-label="Fermer"
          title="Fermer"
          className="absolute top-2 right-2 w-9 h-9 rounded-full text-ocean-500 hover:text-coral-600 hover:bg-coral-50 flex items-center justify-center text-xl font-bold transition disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-ocean-400"
        >
          ✕
        </button>

        <h3 className="text-base sm:text-lg font-bold text-ocean-900 pr-10">
          Que faire pour <span className="text-coral-600">{proposal.prenom}</span> ?
        </h3>
        <p className="text-sm text-ocean-700 leading-snug mt-1">
          Nuit <strong>{debutClick}</strong> — état actuel&nbsp;:{' '}
          <CurrentEtatBadge etat={proposal.current_etat} />
        </p>

        {/* ÉTAPE 1 : choix de l'état */}
        <div className="mt-5 border-2 border-ocean-100 rounded-xl p-3 sm:p-4 bg-ocean-50/30">
          <div className="flex items-start gap-2.5 mb-2">
            <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-ocean-100 text-ocean-700 font-bold text-sm shrink-0">1</span>
            <h4 className="font-bold text-ocean-900 text-sm sm:text-base leading-tight">
              Choisis un état
            </h4>
          </div>
          <div className="grid grid-cols-1 gap-1.5">
            {(['confirme', 'peut_etre', 'refus', 'absent'] as Etat[]).map(e => (
              <EtatChoiceButton
                key={e}
                etat={e}
                isCurrent={proposal.current_etat === e}
                isSelected={chosenEtat === e}
                disabled={working}
                onClick={() => setChosenEtat(e)}
              />
            ))}
          </div>
        </div>

        {/* ÉTAPE 2 : portée */}
        <div className={`mt-3 border-2 rounded-xl p-3 sm:p-4 transition-colors ${chosenEtat ? 'border-coral-200 bg-coral-50/40' : 'border-ocean-100 bg-white/40 opacity-60'}`}>
          <div className="flex items-start gap-2.5 mb-2">
            <span className={`inline-flex items-center justify-center w-7 h-7 rounded-full font-bold text-sm shrink-0 ${chosenEtat ? 'bg-coral-100 text-coral-700' : 'bg-ocean-100 text-ocean-700'}`}>2</span>
            <h4 className={`font-bold text-sm sm:text-base leading-tight ${chosenEtat ? 'text-coral-800' : 'text-ocean-900'}`}>
              Appliquer à…
            </h4>
          </div>
          {!chosenEtat && (
            <p className="text-[12px] text-ocean-600/80 italic mb-2">
              Choisis d'abord un état à l'étape 1.
            </p>
          )}
          <div className="grid grid-cols-1 gap-1.5">
            {/* Option A : nuit cliquée uniquement */}
            <button
              type="button"
              disabled={!chosenEtat || working}
              onClick={() => chosenEtat && onApply(chosenEtat, [proposal.nuit_clickee.id])}
              className="flex items-center gap-2.5 px-3 py-2.5 rounded-lg border-2 border-ocean-300 bg-white hover:bg-ocean-50 text-ocean-900 font-semibold transition disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white"
            >
              <span className="inline-flex items-center justify-center w-7 h-7 rounded-md bg-ocean-100 text-ocean-700 font-bold">1</span>
              <span className="text-left text-sm sm:text-base">
                <span className="block">Seulement à <strong>{debutClick}</strong></span>
                <span className="block text-[11px] text-ocean-600/80 font-normal">la nuit cliquée uniquement</span>
              </span>
            </button>

            {/* Option B : nuit cliquée + toutes les suivantes */}
            <button
              type="button"
              disabled={!chosenEtat || working || n === 0}
              onClick={() =>
                chosenEtat && onApply(chosenEtat, [proposal.nuit_clickee.id, ...nuits_suivantes.map(nu => nu.id)])
              }
              className="flex items-center gap-2.5 px-3 py-2.5 rounded-lg border-2 border-coral-300 bg-coral-50 hover:bg-coral-100 text-coral-900 font-semibold transition disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-coral-50"
            >
              <span className="inline-flex items-center justify-center w-7 h-7 rounded-md bg-coral-200 text-coral-800 font-bold">{n + 1}</span>
              <span className="text-left text-sm sm:text-base">
                <span className="block">À <strong>{debutClick}</strong> + <strong>{n}</strong> nuit{n > 1 ? 's' : ''} suivante{n > 1 ? 's' : ''}</span>
                <span className="block text-[11px] text-coral-700/80 font-normal">
                  {n === 0 ? 'aucune suivante : dernière nuit du séjour' : 'écrase les états déjà saisis sur ces nuits'}
                </span>
              </span>
            </button>

            {/* Option C : plage de dates DÉBUT → FIN — nouveauté demandée */}
            <div className={`rounded-lg border-2 border-sand-300 bg-sand-50/70 px-3 py-2.5 ${!chosenEtat || working ? 'opacity-60' : ''}`}>
              <div className="flex items-center gap-2.5 mb-2">
                <span className="inline-flex items-center justify-center w-7 h-7 rounded-md bg-sand-200 text-sand-800 font-bold shrink-0">{rangeCount}</span>
                <span className="text-left text-sm sm:text-base font-semibold text-sand-900">
                  D'une date à une autre
                </span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto_1fr] gap-2 items-center mb-2">
                <label className="flex flex-col text-[11px] text-sand-800 font-semibold">
                  <span className="mb-0.5">Du</span>
                  <select
                    value={rangeStartIdx}
                    onChange={e => setRangeStartIdx(Number(e.target.value))}
                    disabled={!chosenEtat || working}
                    className="input py-1.5 text-sm font-medium"
                    aria-label="Nuit de début"
                  >
                    {nuits_all.map((nu, i) => (
                      <option key={nu.id} value={i}>{nuitLibelleCourt(nu)}</option>
                    ))}
                  </select>
                </label>
                <span aria-hidden className="text-sand-700 text-center hidden sm:block pt-4">→</span>
                <label className="flex flex-col text-[11px] text-sand-800 font-semibold">
                  <span className="mb-0.5">Au</span>
                  <select
                    value={rangeEndIdx}
                    onChange={e => setRangeEndIdx(Number(e.target.value))}
                    disabled={!chosenEtat || working}
                    className="input py-1.5 text-sm font-medium"
                    aria-label="Nuit de fin"
                  >
                    {nuits_all.map((nu, i) => (
                      <option key={nu.id} value={i}>{nuitLibelleCourt(nu)}</option>
                    ))}
                  </select>
                </label>
              </div>
              <button
                type="button"
                disabled={!chosenEtat || working || rangeCount === 0}
                onClick={() => chosenEtat && onApply(chosenEtat, rangeNuitIds)}
                className="w-full inline-flex items-center justify-center gap-2 px-3 py-2 rounded-md border-2 border-sand-400 bg-white hover:bg-sand-100 text-sand-900 text-sm font-bold transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Appliquer aux <strong>{rangeCount}</strong> nuit{rangeCount > 1 ? 's' : ''} sélectionnée{rangeCount > 1 ? 's' : ''}
              </button>
              <p className="text-[10px] text-sand-700/80 italic mt-1 leading-snug">
                {effStart !== rangeStartIdx || effEnd !== rangeEndIdx
                  ? '(dates réordonnées automatiquement)'
                  : 'inclusif : du 1er au dernier jour sélectionné'}
              </p>
            </div>
          </div>
          {working && (
            <p className="text-[12px] text-coral-700 italic mt-3 text-center font-semibold flex items-center justify-center gap-2">
              <span className="inline-block w-3 h-3 rounded-full border-2 border-coral-600 border-t-transparent animate-spin" />
              Application en cours…
            </p>
          )}
        </div>
      </div>
    </div>
  )
}

function Pill({ label, value }: { label: string; value: string }) {
  return (
    <div className="bg-ocean-50 rounded-lg px-2.5 py-1">
      <div className="text-[9px] uppercase tracking-wide text-ocean-700 font-semibold whitespace-nowrap">{label}</div>
      <div className="font-bold text-ocean-900 leading-tight">{value}</div>
    </div>
  )
}

function FamilleBlock({
  compte_id, nom, personnes, nuits, presenceMap, montantParPers, totalFamille,
  nbColonnes, collapsed, onToggleFamille, onCellClick, pendingKey,
}: {
  compte_id: number
  nom: string
  personnes: PersonneT[]
  nuits: Array<{ id: number; libelle: string }>
  presenceMap: Map<string, Etat>
  montantParPers: Map<number, { du: number; du_max: number; nb_tent: number }>
  totalFamille: {
    nb_nuits: number; montant: number;
    montant_max: number; nb_tentatifs: number;
    nb_pers: number;
  } | null
  nbColonnes: number
  collapsed: boolean
  onToggleFamille: () => void
  /** Clic sur une cellule : ouvre la modale (pas de mutation immédiate). */
  onCellClick: (personne_id: number, cible_id: number) => void
  /** Renvoie true si la cellule (personne, nuit) est en cours de mutation. */
  pendingKey: (personne_id: number, nuit_id: number) => boolean
}) {
  return (
    <>
      <tr className="bg-gradient-sand">
        <td colSpan={nbColonnes + 3} className="px-0 py-0 sticky-col">
          <button
            type="button"
            onClick={onToggleFamille}
            className="w-full flex items-center gap-2 px-2 py-1.5 hover:bg-sand-200/50 transition text-left"
            aria-expanded={!collapsed}
            aria-controls={`fam-${compte_id}`}
          >
            <span className="text-sand-800 text-xs">{collapsed ? '▸' : '▾'}</span>
            <span className="font-bold text-sand-800">👨‍👩‍👧 {nom}</span>
            {totalFamille && (totalFamille.nb_nuits > 0 || totalFamille.nb_tentatifs > 0) && (
              <span className="ml-auto text-right text-[11px] text-sand-700 font-semibold leading-tight">
                <span className="block">
                  {totalFamille.nb_pers} pers · {totalFamille.nb_nuits} nuits · {fmtEur(totalFamille.montant)}
                </span>
                {totalFamille.nb_tentatifs > 0 && (
                  <span className="block text-amber-700">
                    ↗ + {totalFamille.nb_tentatifs} ↔ provisoires = {fmtEur(totalFamille.montant_max)}
                  </span>
                )}
              </span>
            )}
          </button>
        </td>
      </tr>
      {!collapsed && personnes.map(p => {
        // Compte seulement les "confirmé" pour le pricing (peut-être ne compte pas)
        const nb = nuits.reduce(
          (acc, n) => acc + (presenceMap.get(`${p.id}-${n.id}`) === 'confirme' ? 1 : 0),
          0,
        )
        return (
          <tr key={p.id} id={`fam-${compte_id}`}>
            <td className="px-2 py-0.5 sticky-col">
              <div className="flex items-center gap-1.5">
                <Avatar personne_id={p.id} prenom={p.prenom} avatar_version={p.avatar_version} size="xs" />
                <span className="font-medium text-ocean-900 truncate text-[12px] sm:text-sm">{p.prenom}</span>
                {p.categorie !== 'adulte' && (
                  <span className={`tag-${p.categorie} hidden lg:inline-flex shrink-0`}>
                    {p.categorie === 'bebe' ? 'B' : p.categorie === 'enfant' ? 'E' : 'A'}
                  </span>
                )}
              </div>
            </td>
            {nuits.map(n => {
              const etat: Etat = presenceMap.get(`${p.id}-${n.id}`) ?? 'absent'
              const isPending = pendingKey(p.id, n.id)
              const cls =
                etat === 'confirme' ? 'cell-on' :
                etat === 'peut_etre' ? 'cell-tentatif' :
                etat === 'refus' ? 'cell-refus' :
                'cell-off'
              const label =
                etat === 'confirme' ? '✓' :
                etat === 'peut_etre' ? '↔' :
                etat === 'refus' ? '✕' :
                ''
              const aria =
                etat === 'confirme' ? 'Présence confirmée'
                : etat === 'peut_etre' ? 'Présence peut-être (provisoire)'
                : etat === 'refus' ? 'Ne vient pas (refus explicite)'
                : 'Pas encore répondu'
              return (
                <td key={n.id} className="p-0 text-center">
                  <button
                    type="button"
                    onClick={() => onCellClick(p.id, n.id)}
                    disabled={isPending}
                    className={`cell cell-compact relative ${cls} ${isPending ? 'opacity-70 cursor-wait' : ''}`}
                    aria-label={`${p.prenom} ${n.libelle} : ${aria} — clic pour modifier`}
                    title={aria}
                  >
                    {isPending ? <CellSpinner /> : label}
                  </button>
                </td>
              )
            })}
            <td className="px-1 py-0.5 text-right font-semibold text-ocean-900">{nb}</td>
            {(() => {
              const m = montantParPers.get(p.id)
              const du = m?.du ?? 0
              const duMax = m?.du_max ?? 0
              const hasTentatifs = (m?.nb_tent ?? 0) > 0
              return (
                <td
                  className="px-1 py-0.5 text-right leading-tight"
                  title={hasTentatifs
                    ? `Confirmé : ${fmtEur(du)} — Si tu confirmes aussi tes ${m!.nb_tent} « peut-être » : ${fmtEur(duMax)}`
                    : `Dû : ${fmtEur(du)}`}
                >
                  <div className="font-bold text-ocean-900">{fmtEur(du)}</div>
                  {hasTentatifs && (
                    <div className="text-[9px] text-amber-700 font-semibold">
                      ↗ {fmtEur(duMax)} si ↔ confirmés
                    </div>
                  )}
                </td>
              )
            })()}
          </tr>
        )
      })}
      {!collapsed && totalFamille && (totalFamille.nb_nuits > 0 || totalFamille.nb_tentatifs > 0) && (
        <tr className="bg-coral-50/60 border-t-2 border-coral-200">
          <td className="px-2 py-1 sticky-col bg-coral-50/60 text-right text-[10px] uppercase tracking-wide font-bold text-coral-700">
            Total {nom}
          </td>
          <td colSpan={nuits.length} className="text-right pr-1 text-[10px] text-coral-700 font-semibold">
            {totalFamille.nb_nuits} cumul. →
          </td>
          <td className="px-1 py-1 text-right font-bold text-coral-700">{totalFamille.nb_nuits}</td>
          <td className="px-1 py-1 text-right leading-tight">
            <div className="font-bold text-coral-700">{fmtEur(totalFamille.montant)}</div>
            {totalFamille.nb_tentatifs > 0 && (
              <div className="text-[9px] text-amber-700 font-semibold">
                ↗ {fmtEur(totalFamille.montant_max)} si ↔ confirmés
              </div>
            )}
          </td>
        </tr>
      )}
    </>
  )
}
